import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CurrencyAmountUI,
  DepositCard,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  PayoutCard,
  PrimaryFullScreenSpinner,
  TransferCard,
  Txt,
  toast,
} from "@repo/ui";
import { NotFoundError, neverFunc } from "@repo/utils";
import {
  createFileRoute,
  Link,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { Suspense, useState } from "react";
import { RiArrowLeftFill } from "react-icons/ri";
import { AssetRulesCard } from "~/components/Asset/AssetRulesCard";
import { AssetTransactionsCard } from "~/components/Asset/AssetTransactionsCard";
import { AssetBalanceChart } from "~/components/Asset/BalanceChart";
import { ExternalBankAccountAssetDetailsCard } from "~/components/Asset/ExternalBankAccountAssetDetails";
import { ExternalBankAccountAssetMonthlyInsightsCard } from "~/components/Asset/ExternalBankAccountAssetMonthlyInsightsCard";
import { FixedIncomeFundAssetDetailsCard } from "~/components/Asset/FixedIncomeFundAssetDetails";
import { FXAssetDetailsCard } from "~/components/Asset/FXAssetDetails";
import { NoticeAccountAssetDetailsCard } from "~/components/Asset/NoticeAccountAssetDetails";
import { PaymentIdentifiersCard } from "~/components/Asset/PaymentIdentifiers";
import { SavingsAssetDetailsCard } from "~/components/Asset/SavingsAssetDetails";
import { TermDepositAssetDetailsCard } from "~/components/Asset/TermDepositAssetDetails";
import { AdminCurrencycloudBeneficiaryCard } from "~/components/Currencycloud/AdminCurrencycloudBeneficiaryCard";
import { AdminEburyBeneficiaryCard } from "~/components/Ebury/AdminEburyBeneficiaryCard";
import { AssetLedgerAccountCard } from "~/components/Ledger/LedgerAccountCard";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import type { AdminAssetDetailQuery } from "~/gql/client/graphql";
import { opsHead, useOpsTitle } from "~/head";

export const Route = createFileRoute("/__auth/assets_/$assetId")({
  head: () => opsHead("Asset"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <AssetDetailPage />
    </Suspense>
  ),
});

const AssetDetailPage = () => {
  const params = useParams({ strict: false });
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const navigate = useNavigate();

  const { data, error } = useSuspenseQuery(
    graphql(`
      query AdminAssetDetail($id: ID!) {
        asset(id: $id) {
          id
          class
          country
          currency
          feeRate
          transactionFeeRate
          name
          openedAt
          status
          settlementAccount {
            ... on FXAsset {
              ...AssetCardFields
            }
            ... on SavingsAsset {
              ...AssetCardFields
            }
            ... on BeneficiaryAccount {
              ...BeneficiaryAssetCardFields
            }
          }
          estimatedValue {
            amount
            currency
          }
          estimatedValueChangeOverMonth {
            amount
            currency
          }
          estimatedValueWithOpenFundMovements {
            amount
            currency
          }
          openFundMovements {
            ... on Transfer {
              id
              ...TransferCardFields
            }
            ... on Payout {
              id
              ...PayoutCardFields
            }
            ... on Deposit {
              id
              ...DepositCardFields
            }
          }

          assetRules {
            ...AssetRuleCardFields
          }

          assetLedgerAccounts {
            id
            ... AssetLedgerAccountCardFields
          }

          businessEntity {
            id
            currencycloudAccount {
              id
            }
            organisation {
              id
              ... OrganisationCardFields
              businessEntities {
                id
                name
                eburyClient {
                  id
                }
                currencycloudAccount {
                  id
                }
              }
              assets {
                  id
                  name
                  businessEntity {
                      id
                      name
                  }
              }
            }
            ... BusinessEntityCardFields
          }

          custodyAccount {
            id
            shortRef
          }

          ... @defer {
            eburyBeneficiaries {
              id
              businessEntity {
                id
              }
              ...AdminEburyBeneficiaryCard
            }
          }

          ... @defer {
            currencycloudBeneficiaries {
              id
              businessEntity {
                id
              }
              ...AdminCurrencycloudBeneficiaryCard
            }
          }

          paymentIdentifiers {
            id
            ... PaymentIdentifierComponent
          }

          ... on FixedIncomeFundAsset {
            apiOrdersSupported
            product {
              id

              provider {
                ... ProviderCardFields
              }

              ... ProductCardFields
            }
          }

          ... on FXAsset {
            eburyAccount {
              id
              active
              clientId
            }
            currencycloudBalance {
              id
              account {
                id
              }
            }
            currencycloudAccount {
              id
            }
            provider {
              id
              name
              internalName
            }
          }

          ... on ExternalBankAccountAsset {
            id
            details {
              id
            }
            beneficiaryAccount {
              id
              ... BeneficiaryAssetCardFields
            }
            ...ExternalBankAccountAssetMonthlyInsightsFields
          }

          ... on NoticeAccountAsset {
            product {
              id
              provider {
                ... ProviderCardFields
              }
              ... ProductCardFields
            }
          }

          ... on SavingsAsset {
            product {
              id
              provider {
                ... ProviderCardFields
              }
              ... ProductCardFields
            }
          }

          ... on TermDepositAsset {
            maturityInstructions {
              ...on TermDepositMaturityInstructionsRolloverPrinciple {
                  toAsset {
                      id
                      ... AssetCardFields
                  }
              }
              ...on TermDepositMaturityInstructionsTransfer {
                  toAsset {
                      id
                      ... AssetCardFields
                  }
              }
            }

            product {
              id
              provider {
                ... ProviderCardFields
              }
              ... ProductCardFields
            }
          }
        }
      }
      `),
    { variables: { id: params.assetId ?? "" } },
  );

  const [deleteAsset, { loading: deleteAssetLoading }] = useMutation(
    graphql(`
     mutation DeleteAsset($input: DeleteAssetInput!) {
       deleteAsset(input: $input) {
         organisation {
           id
         }
       }
     }
    `),
    {
      variables: {
        input: {
          assetId: params.assetId ?? "",
        },
      },
      onCompleted: (data) => {
        toast({
          kind: "success",
          autoHide: true,
          title: "Asset deleted",
        });
        navigate({
          to: "/organisations/$organisationId",
          params: { organisationId: data.deleteAsset.organisation.id },
        });
      },
      onError: (err) => {
        toast({
          kind: "error",
          autoHide: false,
          title: "Something went wrong deleting asset",
          description: err.message,
        });
        setConfirmDialogOpen(false);
      },
    },
  );

  const asset = data?.asset;
  if (!asset) {
    throw new NotFoundError();
  }

  useOpsTitle(`${asset.businessEntity?.name ?? ""} • ${asset.name}`);

  const fxBusinessEntities = (
    asset.businessEntity?.organisation?.businessEntities ?? []
  ).filter(
    (be) =>
      (be.eburyClient !== null || be.currencycloudAccount !== null) &&
      // for FX assets, exclude the self business entity
      !(asset.__typename === "FXAsset" && be.id === asset.businessEntity?.id),
  );

  return (
    <AdminPageContainer>
      <AdminPageHeader
        title={`${asset.businessEntity?.name ?? ""} • ${asset.name}`}
        subtitle={asset.id}
      />
      <div className="flex flex-col gap-6">
        <div className="flex flex-row gap-4">
          <AssetDetailsCard asset={asset} className="flex-grow" />

          <PaymentIdentifiersCard asset={asset} />
        </div>

        <AssetRulesCard rules={asset.assetRules ?? []} />

        <AssetCurrentBalanceDetails asset={asset} />

        <OpenFundMovementsCard asset={asset} />

        <AssetBalanceChart assetId={asset.id} />

        {asset.__typename === "ExternalBankAccountAsset" && (
          <ExternalBankAccountAssetMonthlyInsightsCard asset={asset} />
        )}

        <AssetTransactionsCard
          assetId={asset.id}
          orgId={asset.businessEntity?.organisation?.id ?? ""}
          assetName={asset.name}
          assetClass={asset.class}
        />

        <div className="flex flex-row gap-4">
          {asset.__typename !== "ExternalBankAccountAsset" &&
            fxBusinessEntities.length > 0 && (
              <Card variant="panel">
                <CardHeader>
                  <CardTitle>
                    <Txt variant="card-title">FX provider beneficiaries</Txt>
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col gap-4">
                  <Txt variant="body">
                    Sync this asset to allow it to be used as a beneficiary in
                    the respective Visa or Ebury clients
                  </Txt>

                  {fxBusinessEntities.map((be) => {
                    if (be.eburyClient) {
                      // apollo defer returns undefined while loading
                      const eburyBeneficiary =
                        asset.eburyBeneficiaries === undefined
                          ? undefined
                          : (asset.eburyBeneficiaries?.find(
                              (b) => b.businessEntity?.id === be.id,
                            ) ?? null);
                      return (
                        <div key={be.id} className="flex flex-col gap-2">
                          <div className="font-semibold text-sm">
                            {be.name} (Ebury {be.eburyClient?.id})
                          </div>

                          <AdminEburyBeneficiaryCard
                            eburyBeneficiary={eburyBeneficiary}
                            eburyBusinessEntityId={be.id}
                            assetId={asset.id}
                            errors={error?.graphQLErrors?.filter((gqlError) => {
                              return (
                                gqlError.path?.at(0) === "asset" &&
                                gqlError.path?.at(1) === "eburyBeneficiaries"
                              );
                            })}
                          />
                          <hr />
                        </div>
                      );
                    } else if (be.currencycloudAccount) {
                      const currencycloudBeneficiary =
                        asset.currencycloudBeneficiaries === undefined
                          ? undefined
                          : (asset.currencycloudBeneficiaries?.find(
                              (b) => b.businessEntity?.id === be.id,
                            ) ?? null);

                      return (
                        <div key={be.id} className="flex flex-col gap-2">
                          <div className="font-semibold text-sm">
                            {be.name} (Visa {be.currencycloudAccount?.id})
                          </div>

                          <AdminCurrencycloudBeneficiaryCard
                            currencycloudBeneficiary={currencycloudBeneficiary}
                            currencycloudBusinessEntityId={be.id}
                            assetId={asset.id}
                            errors={error?.graphQLErrors?.filter((gqlError) => {
                              return (
                                gqlError.path?.at(0) === "asset" &&
                                gqlError.path?.at(1) ===
                                  "currencycloudBeneficiaries"
                              );
                            })}
                          />
                          <hr />
                        </div>
                      );
                    }
                  })}
                </CardContent>
              </Card>
            )}

          <Card className="flex-grow" variant="panel">
            <CardHeader>
              <CardTitle>
                <Txt variant="card-title">Ledger Accounts</Txt>
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              {asset.assetLedgerAccounts?.map((assetLedgerAccount) => (
                <AssetLedgerAccountCard
                  {...assetLedgerAccount}
                  key={assetLedgerAccount.id}
                />
              ))}
            </CardContent>
          </Card>
        </div>
        <div className="flex flex-row justify-end">
          {asset.class !== "EXTERNAL_BANK_ACCOUNT" && (
            <Dialog
              open={confirmDialogOpen}
              onOpenChange={setConfirmDialogOpen}
            >
              <DialogTrigger asChild>
                <Button
                  onClick={() => setConfirmDialogOpen(true)}
                  variant="destructive"
                >
                  Delete asset
                </Button>
              </DialogTrigger>

              <DialogContent hideCloseButton>
                <DialogHeader>
                  <DialogTitle>Permenantly delete {asset.name}</DialogTitle>
                  <DialogDescription>
                    <strong>ATTENTION</strong>
                    <br /> This will permenantly delete this asset from the
                    database. It can not be recovered after this action has been
                    performed.
                  </DialogDescription>
                </DialogHeader>

                <DialogFooter className="gap-2 sm:justify-between">
                  <Button
                    leftIcon={<RiArrowLeftFill />}
                    className="px-6"
                    size="lg"
                    variant="secondary"
                    onClick={(e) => {
                      setConfirmDialogOpen(false);
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                  >
                    {" "}
                    Back
                  </Button>

                  <div className="flex flex-row items-center gap-x-4">
                    <Button
                      variant="destructive"
                      className="px-6"
                      size="lg"
                      loading={deleteAssetLoading}
                      onClick={async (e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        deleteAsset();
                      }}
                    >
                      Confirm deletion
                    </Button>
                  </div>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>
    </AdminPageContainer>
  );
};

function AssetCurrentBalanceDetails({
  asset,
}: {
  asset: NonNullable<AdminAssetDetailQuery["asset"]>;
}) {
  return (
    <Card variant="panel">
      <CardContent className="flex items-start justify-between p-6">
        <div className="flex flex-col gap-1">
          <Txt variant="detail-label">Current balance</Txt>
          <Txt variant="metric">
            <CurrencyAmountUI
              amount={asset.estimatedValue?.amount}
              currency={asset.estimatedValue?.currency}
            />
          </Txt>
        </div>

        <div className="flex flex-col gap-1">
          <Txt variant="detail-label">
            Balance (including open fund movements)
          </Txt>
          <Txt variant="metric">
            <CurrencyAmountUI
              amount={asset.estimatedValueWithOpenFundMovements?.amount}
              currency={asset.estimatedValueWithOpenFundMovements?.currency}
            />
          </Txt>
        </div>

        <div className="flex flex-col gap-1">
          <Txt variant="detail-label">Change 1 month</Txt>
          <Txt variant="metric">
            <CurrencyAmountUI
              amount={asset.estimatedValueChangeOverMonth?.amount}
              currency={asset.estimatedValueChangeOverMonth?.currency}
              showSign
            />
          </Txt>
        </div>
      </CardContent>
    </Card>
  );
}

function OpenFundMovementsCard({
  asset,
}: {
  asset: NonNullable<AdminAssetDetailQuery["asset"]>;
}) {
  const openFundMovements = asset.openFundMovements ?? [];

  const fundMovementCards = openFundMovements.flatMap((fundMovement) => {
    switch (fundMovement.__typename) {
      case "Transfer": {
        const isTransferOut = fundMovement.sourceAsset?.id === asset.id;
        const isTransferIn = fundMovement.destinationAsset?.id === asset.id;
        if (!isTransferIn && !isTransferOut) {
          return [];
        }
        let variant: "TRANSFER_IN" | "TRANSFER_OUT";
        if (isTransferOut && isTransferIn) {
          variant = "TRANSFER_OUT";
        } else if (isTransferOut) {
          variant = "TRANSFER_OUT";
        } else {
          variant = "TRANSFER_IN";
        }
        return [
          <div key={`${fundMovement.__typename}-${fundMovement.id}`}>
            <Link to={`/transfers/${fundMovement.id}`}>
              <TransferCard
                transfer={fundMovement}
                variant={variant}
                subText={["DATE", "PAYMENT_REFERENCE"]}
              />
            </Link>
          </div>,
        ];
      }
      case "Payout":
        return [
          <div key={`${fundMovement.__typename}-${fundMovement.id}`}>
            <Link to={`/payouts/${fundMovement.id}`}>
              <PayoutCard
                payout={fundMovement}
                subText={["DATE", "PAYMENT_REFERENCE"]}
              />
            </Link>
          </div>,
        ];
      case "Deposit":
        return [
          <div key={`${fundMovement.__typename}-${fundMovement.id}`}>
            <Link to={`/deposits/${fundMovement.id}`}>
              <DepositCard deposit={fundMovement} />,
            </Link>
          </div>,
        ];
      case "EburyFXTrade":
      case "FundUnitOrder":
        return [];
      default:
        return neverFunc(fundMovement);
    }
  });

  return (
    <Card variant="panel">
      <CardHeader>
        <CardTitle>
          <Txt variant="card-title">Open fund movements</Txt>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {fundMovementCards.length > 0 ? (
          fundMovementCards
        ) : (
          <Txt variant="body">No open fund movements</Txt>
        )}
      </CardContent>
    </Card>
  );
}

function AssetDetailsCard({
  asset,
  className,
}: {
  asset: NonNullable<AdminAssetDetailQuery["asset"]>;
  className?: string;
}) {
  switch (asset.__typename) {
    case "ExternalBankAccountAsset":
      return (
        <ExternalBankAccountAssetDetailsCard
          asset={asset}
          className={className}
        />
      );
    case "FXAsset":
      return <FXAssetDetailsCard asset={asset} className={className} />;
    case "FixedIncomeFundAsset":
      return (
        <FixedIncomeFundAssetDetailsCard asset={asset} className={className} />
      );
    case "NoticeAccountAsset":
      return (
        <NoticeAccountAssetDetailsCard asset={asset} className={className} />
      );
    case "SavingsAsset":
      return <SavingsAssetDetailsCard asset={asset} className={className} />;
    case "TermDepositAsset":
      return (
        <TermDepositAssetDetailsCard asset={asset} className={className} />
      );
  }
}
